/**
 * Deep links into a cart (Bill 2026-10-14). Links built by teamsAppDeepLink / the API's
 * CardTemplateHelpers.TeamsAppDeepLink carry {"subEntityId":"cart:<guid>"} (older ones
 * "cart/<guid>"); Teams hands it to the app as context.page.subPageId. Teams does NOT carry it
 * into a pop-out window, so PopOutGate passes it on the pop-out's URL as ?open=cart:<guid>
 * (the LWP pattern). Both sources are read here; only the cart shape is honoured, once per load.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { getTeamsContext, initTeams } from '@/services/teams';

let handled = false;
const CART = /^cart[:/]([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

/** The cart id a deep-link token names, or null. Accepts "cart:<guid>" and the older "cart/<guid>". */
export function cartIdFromDeepLink(token: string | null | undefined): string | null {
  const m = CART.exec((token ?? '').trim());
  return m?.[1] ?? null;
}

/** The target in this window's URL (?open=cart:<guid>), set when PopOutGate popped the app out. */
export function deepLinkFromUrl(): string | null {
  try {
    const v = new URLSearchParams(window.location.search).get('open');
    return cartIdFromDeepLink(v) ? v : null;
  } catch {
    return null;
  }
}

/** The target Teams gave this window (subPageId), if it names a cart. */
export async function deepLinkFromTeams(): Promise<string | null> {
  try {
    await initTeams();
    const sub = getTeamsContext()?.page?.subPageId ?? null;
    return cartIdFromDeepLink(sub) ? sub : null;
  } catch {
    return null;
  }
}

export function useTeamsDeepLink(): void {
  const navigate = useNavigate();
  useEffect(() => {
    if (handled) return;
    let live = true;
    void (async () => {
      const token = deepLinkFromUrl() ?? (await deepLinkFromTeams());
      if (!live || handled) return;
      handled = true;
      const id = cartIdFromDeepLink(token);
      if (id) navigate(`/rao/${encodeURIComponent(id)}`);
    })();
    return () => { live = false; };
  }, [navigate]);
}
